import React from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {getAccommodationPageHotel} from 'modules/searchApi/selectors'

import {ButtonLink} from '@vio/atlas/ButtonLink'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {NoStyleLink} from '@vio/atlas/helpers/NoStyleLink'
import {cssTheme} from '@vio/atlas/src/themes'
import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {RoomSplitType} from '@vio/core/src/room/types/RoomConfiguration'
import {useRoomDisplayName} from '@vio/shared/src/accommodation/RoomDisplayName'
import {RoomInfo} from '@vio/shared/src/accommodation/RoomInfo'
import {
  getTotalAdults,
  getTotalChildren
} from '@vio/shared/src/accommodation/RoomInfo/utils'
import {RoomNameWithMultiplier} from '@vio/shared/src/accommodation/RoomNameWithMultiplier'

import {useRoomTitleConfig} from '../hooks/useRoomTitleConfig'

import type {Room} from '@vio/core/src/room/types/room'

type Props = {
  room: Room
  onOpenRoomDetailsOverlay?: () => void
  isDetailsLinkVisible?: boolean
  userCountry: string
  roomsSplit: RoomSplitType[]
}

const MultilineRoomName = styled(Text)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
  -webkit-line-clamp: 4;
  line-height: ${cssTheme.typography.titleL.lineHeight} !important;
`
const RoomDetailsLinkWrapper = styled(ButtonLink)`
  flex-shrink: 0;
`
const RoomDetailsLink = () => (
  <RoomDetailsLinkWrapper variant="primary" size="lg" isCompact>
    <Text variant="labelS">
      <FormattedMessageWrapper id="roomDetails" defaultMessage="Room details" />
    </Text>
  </RoomDetailsLinkWrapper>
)
const LONG_ROOM_NAME_LENGTH = 40

const RoomNameFitted = ({
  roomName,
  size
}: {
  roomName: string
  size: 'compact' | 'regular'
}) => {
  return (
    <MultilineRoomName
      variant={size === 'compact' ? 'titleM' : 'titleL'}
      colorPath="content.neutral.primary"
      dataId="roomInfo.roomName"
      tag="h3"
    >
      {roomName}
    </MultilineRoomName>
  )
}

export const RoomCardInfo = ({
  room,
  onOpenRoomDetailsOverlay,
  isDetailsLinkVisible = false,
  userCountry,
  roomsSplit
}: Props) => {
  const {isMobile, isDesktopXs} = useDeviceLayout()
  const isMobileOrTablet = isMobile || isDesktopXs
  const roomName = useRoomDisplayName(room.name, {
    numberOfGuests: getTotalAdults(roomsSplit) + getTotalChildren(roomsSplit),
    numberOfRooms: roomsSplit.length
  })

  const size = roomName.length > LONG_ROOM_NAME_LENGTH ? 'compact' : 'regular'
  const {numberOfRooms, isRoomInfoEnabled, hasAllRoomsHaveLabel} =
    useRoomTitleConfig(room, roomsSplit.length)
  const hotel = useSelector(getAccommodationPageHotel)
  const childPolicy = hotel?.childPolicy
  const allRoomsHaveLabelPlacement = isMobileOrTablet ? 'above' : 'inline'
  const LinkWrapper = ({children}: {children: React.ReactNode}) => {
    if (isDetailsLinkVisible) {
      return (
        // Rendered as a div: the children include block-level elements and
        // another link, which browsers reparent inside an <a>, so SSR markup
        // and the client tree stop matching and hydration throws.
        <NoStyleLink as="div" onClick={onOpenRoomDetailsOverlay}>
          {children}
        </NoStyleLink>
      )
    }

    // keep the div to see it as one element by flex parents
    return <div>{children}</div>
  }

  return (
    <LinkWrapper>
      <Box
        justifyContent="space-between"
        alignItems="flex-start"
        gap="s300"
        display="flex"
        paddingBottom="s250"
      >
        {numberOfRooms > 1 ? (
          <RoomNameWithMultiplier
            roomNameSlot={<RoomNameFitted roomName={roomName} size={size} />}
            numberOfRooms={numberOfRooms}
          />
        ) : (
          <RoomNameFitted roomName={roomName} size={size} />
        )}
        {isDetailsLinkVisible && !isMobileOrTablet && <RoomDetailsLink />}
      </Box>
      <RoomInfo
        room={room}
        userCountry={userCountry}
        roomsSplit={roomsSplit}
        childPolicy={childPolicy}
        allRoomsHaveLabel={
          hasAllRoomsHaveLabel ? allRoomsHaveLabelPlacement : undefined
        }
        hasCompactAmenities={isRoomInfoEnabled && isMobileOrTablet}
      />
      {isDetailsLinkVisible && isMobileOrTablet && (
        <Box paddingTop="s400">
          <RoomDetailsLink />
        </Box>
      )}
    </LinkWrapper>
  )
}
